/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Cards } from "../components/Cards.tsx";
import ActionDropdown from "../components/ActionDropdown.tsx";
import { CareNoteModal } from "../components/modals/CareNoteModal.tsx";
import { getStaffShifts, updateShift } from "../services/shifts.js";
import Spinner from "../components/PageLoader.js";
import { useNavigate, useParams } from "react-router-dom";
import {
  capitalize,
  convertLocalToUTC,
  dateFormat,
  formatUKTime,
  formatUTCForDB,
} from "../utils/functions.js";
import { getServiceUser } from "../services/users.js";
import { WarningModal } from "../components/modals/WarningModal.js";
import toast from "react-hot-toast";
import { AdministerMedication } from "../components/modals/AdministerMedication.js";

export const FieldShiftsDetails = () => {
  const [showCareNoteModal, setShowCareNoteModal] = useState(false);
  const [shift, setShift] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [serviceUser, setServiceUser] = useState(null);
  const [openWarning, setOpenWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [action, setAction] = useState("");
  const [careNote, setCareNote] = useState("");
  const [openAdministerModal, setOpenAdministerModal] = useState(false);

  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const urlParam = useParams();
  const shiftId = urlParam.id;
  const serviceUserId = urlParam.serviceUserId;

  const actions = [
    {
      label: "Clock in",
      onClick: () => handleClockIn(),
      visible: !shift?.clockedIn,
    },
    {
      label: "Clock out",
      onClick: () => handleClockOut(),
      visible: Boolean(shift?.clockedIn && !shift?.clockedOut),
    },
    {
      label: "Care note",
      onClick: () => handleCareNoted(),
      visible: Boolean(shift?.clockedIn),
    },
    {
      label: "Medication",
      onClick: () => handleMedication(),
      visible: Boolean(shift?.clockedIn && shift?.medicationGroupId),
    },
    {
      label: "PRN",
      onClick: () => handlePRNMedication(),
      visible: Boolean(shift?.clockedIn),
    },
  ];

  const handleClockIn = () => {
    if (shift?.clockedIn) {
      toast.error("You are already clocked in");
      return;
    }
    setWarningMessage("Are you sure you want to clock in?");
    setOpenWarning(true);
    setAction("clockedIn");
  };

  const handleClockOut = () => {
    if (shift?.clockedOut) {
      toast.error("You are already clocked out");
      return;
    }
    if (!shift?.clockedIn) {
      toast.error("You must clock in first");
      return;
    }
    if (shift?.latestCareNote[0]?.note === "") {
      toast.error("You must add a care note first");
      return;
    }

    setWarningMessage("Are you sure you want to clock out?");
    setOpenWarning(true);
    setAction("clockedOut");
  };

  const handleCareNoted = () => {
    if (!shift?.clockedIn) {
      toast.error("You must clock in first");
      return;
    }
    setShowCareNoteModal(true);
  };

  const handleMedication = () => {
    if (!shift?.clockedIn) {
      toast.error("You must clock in first");
      return;
    }
    navigate(`/field-shifts/${shiftId}/medication/${serviceUserId}`);
    // setOpenAdministerModal(true);
  };

  const handlePRNMedication = () => {
    if (!shift?.clockedIn) {
      toast.error("You must clock in first");
      return;
    }
    navigate(`/field-shifts/${shiftId}/prn/${serviceUserId}`);
  };

  const fetchShifts = async () => {
    setIsLoading(true);
    try {
      const { data } = await getStaffShifts({ userId: user.username || "" });
      const shift = data.find((shift) => shift.shiftId === Number(shiftId));
      setShift(shift);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchServiceUser = async () => {
    try {
      const { data } = await getServiceUser(serviceUserId);
      setServiceUser(data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchShifts(), fetchServiceUser()]);
  }, []);

  const updateStaffShift = async (update) => {
    setIsLoading(true);
    try {
      const payload = {
        shiftId: shift.shiftId,
        ...update,
      };
      await updateShift(payload);
      fetchShifts();
      toast.success("Shift updated successfully");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const submitCareNote = async (e) => {
    setCareNote(e);
    updateStaffShift({ careNotes: e });
    setShowCareNoteModal(false);
  };

  const warningAction = () => {
    if (action === "clockedIn") {
      const clockedInDate = formatUTCForDB(
        convertLocalToUTC(new Date()),
        "yyyy-MM-dd HH:mm:ss"
      );
      updateStaffShift({ clockedIn: clockedInDate, status: "inprogress" });
    } else if (action === "clockedOut") {
      const clockedOutDate = formatUTCForDB(
        convertLocalToUTC(new Date()),
        "yyyy-MM-dd HH:mm:ss"
      );
      updateStaffShift({ clockedOut: clockedOutDate, status: "completed" });
    }
    setOpenWarning(false);
  };

  const shouldShowViewButton = (shift) => {
    const now = new Date();
    const startTime = new Date(shift.shiftStart);
    const endTime = new Date(shift.shiftEnd);

    // Calculate time differences in milliseconds
    const msUntilStart = startTime - now;
    const msSinceEnd = now - endTime;

    // Convert to more readable units
    const minutesUntilStart = msUntilStart / 1000 / 60;
    const daysSinceEnd = msSinceEnd / 1000 / 60 / 60 / 24;

    return (
      (minutesUntilStart <= 15 && minutesUntilStart >= 0) ||
      (now >= startTime && now <= endTime) ||
      (daysSinceEnd <= 2 && daysSinceEnd >= 0)
    );
  };

  return (
    <>
      <div className="profile-details">
        {shift &&
          shift?.status !== "completed" &&
          shouldShowViewButton(shift) && <ActionDropdown actions={actions} />}
        {shift && (
          <Cards classes="mb-24">
            <h5 className="mb-8">Details</h5>

            <div className="field-shift__details">
              <div className="form__group">
                <label>Visit for</label>
                <p>
                  {`${shift?.serviceUser?.firstName} ${shift?.serviceUser?.lastName}`}
                </p>
              </div>
              <div className="form__group">
                <label>Shift type</label>
                <p>{shift?.shiftType}</p>
              </div>
              <div className="form__group">
                <label>Call</label>
                <p>{shift?.call}</p>
              </div>
              <div className="form__group">
                <label>Start</label>
                <p>{formatUKTime(shift?.shiftStart, "hh:mmaaa")}</p>
              </div>
              <div className="form__group">
                <label>End</label>
                <p>{formatUKTime(shift?.shiftEnd, "hh:mmaaa")}</p>
              </div>
              <div className="form__group">
                <label>Clock in</label>
                <p>{formatUKTime(shift?.clockedIn, "hh:mmaaa")}</p>
              </div>
              <div className="form__group">
                <label>Clock out</label>
                <p>{formatUKTime(shift?.clockedOut, "hh:mmaaa")}</p>
              </div>
              <div className="form__group">
                <label>Status</label>
                <p>{capitalize(shift?.status)}</p>
              </div>
            </div>
            <div className="form__group">
              <label>Care note</label>
              <p>{shift.latestCareNote[0]?.note || "-"}</p>
            </div>
          </Cards>
        )}

        {serviceUser && (
          <Cards>
            <h5 className="mb-8">Client details</h5>

            <div className="field-shift__details">
              <div className="form__group">
                <label>Date of birth</label>
                <p>
                  {dateFormat(serviceUser?.dateOfBirth, "do MMM, yyyy" || "--")}
                </p>
              </div>
              <div className="form__group">
                <label>Address</label>
                <p>{serviceUser?.address}</p>
              </div>
              <div className="form__group">
                <label>Postal code</label>
                <p>{serviceUser?.postalCode}</p>
              </div>
              <div className="form__group">
                <label>Phone</label>
                <p>{serviceUser?.telephoneNumber}</p>
              </div>
              <div className="form__group">
                <label>Gender</label>
                <p>{capitalize(serviceUser?.gender)}</p>
              </div>
            </div>
          </Cards>
        )}
      </div>
      {isLoading && (
        <div className="documents__loader">
          <Spinner />
        </div>
      )}
      {showCareNoteModal && (
        <CareNoteModal
          showCareNoteModal={showCareNoteModal}
          setShowCareNoteModal={setShowCareNoteModal}
          submitCareNote={submitCareNote}
          setCareNote={setCareNote}
          careNote={careNote}
          isLoading={isLoading}
        />
      )}

      {openWarning && (
        <WarningModal
          isOpen={openWarning}
          onCancel={() => setOpenWarning(false)}
          title="Warning"
          isLoading={isLoading}
          message={warningMessage}
          onConfirm={warningAction}
        />
      )}

      {openAdministerModal && (
        <AdministerMedication
          isOpen={openAdministerModal}
          onClose={() => setOpenAdministerModal(false)}
          shift={shift}
          medication={serviceUser?.calls}
        />
      )}
    </>
  );
};
